import { useCallback, useEffect, useRef, useState } from 'react';
import { publicOrigin } from '../lib/desktop';
import { MOUVEMENT_VITESSE_MIN_KMH, estPositionFraiche } from '../components/map/fleetRules';
import { enDate } from '../lib/formats';

/**
 * The single tracking socket of the front end.
 *
 * Three screens each opened their own WebSocket to the same endpoint, each
 * with its own reconnection policy, its own merge logic and its own polling
 * fallback. One of them applied the incoming batch with a `setState` per point
 * inside a `forEach`, which re-rendered the whole map once per truck per
 * message.
 */

export const BASE_RECONNECT_DELAY_MS = 1000;
export const MAX_RECONNECT_DELAY_MS = 30000;
const KEEPALIVE_MS = 25000;

/**
 * Exponential backoff with full jitter. A proxy restart drops every open
 * dashboard at the same instant; the previous fixed 5 s retry brought them all
 * back in the same millisecond and knocked the backend over a second time.
 */
export function nextReconnectDelay(attempt, random = Math.random) {
  const capped = Math.min(
    MAX_RECONNECT_DELAY_MS,
    BASE_RECONNECT_DELAY_MS * 2 ** Math.max(0, attempt),
  );
  return Math.round(capped / 2 + random() * (capped / 2));
}

/**
 * The endpoint authenticates from a query parameter, so the JWT lands in every
 * proxy access log between the browser and the server. Moving it to a
 * short-lived ticket or a subprotocol needs the backend to accept one; until
 * then at least it is built in exactly one place.
 */
export function buildStreamUrl({ mode = 'fleet', tripId, token, origin } = {}) {
  if (!token) return null;
  if (mode === 'trip' && !tripId) return null;
  const base =
    origin ||
    process.env.REACT_APP_BACKEND_URL ||
    // Dans l'application Windows, l'origine de la page (app://) n'est pas un serveur.
    publicOrigin();
  const wsBase = String(base).replace(/^https/, 'wss').replace(/^http/, 'ws');
  const path =
    mode === 'trip'
      ? `/api/v1/tracking/ws/tracking/${encodeURIComponent(tripId)}`
      : '/api/v1/tracking/ws/tracking/fleet';
  return `${wsBase}${path}?token=${encodeURIComponent(token)}`;
}

/**
 * A stream point carries a position, not a fleet record. Merging it wholesale
 * blanked the plate and the driver name that only the REST snapshot supplies,
 * and the list under the map lost its labels a few seconds after loading.
 *
 * « En ligne » se déduit de l'horodatage du point (règle partagée
 * `estPositionFraiche`), jamais du simple fait d'avoir reçu un message : le
 * message initial du socket rejoue la dernière position connue de chaque
 * camion, souvent vieille de plusieurs heures. Un point plus ancien que celui
 * que l'on détient déjà est ignoré : il ne doit pas faire reculer le camion.
 */
export function mergeVehicle(existing, point, maintenant = Date.now()) {
  const incoming = enDate(point?.timestamp);
  const known = enDate(existing?.timestamp);
  if (incoming && known && incoming.getTime() < known.getTime()) return existing;

  const patch = {};
  Object.keys(point).forEach((key) => {
    if (point[key] !== undefined) patch[key] = point[key];
  });
  if (!patch.truck || Object.keys(patch.truck).length === 0) delete patch.truck;
  if (!patch.driver || Object.keys(patch.driver).length === 0) delete patch.driver;
  const merged = { ...existing, ...patch };
  // « À l'arrêt depuis » : le serveur le calcule pour l'instantané, le flux le
  // tient à jour. Un point qui roule l'efface ; le premier point immobile
  // après un mouvement ouvre l'arrêt à son heure.
  if (point.stopped_since === undefined && point.speed_kmh != null) {
    const vitesse = Number(point.speed_kmh);
    if (Number.isFinite(vitesse) && vitesse > MOUVEMENT_VITESSE_MIN_KMH) merged.stopped_since = null;
    else if (Number.isFinite(vitesse) && !existing?.stopped_since) merged.stopped_since = point.timestamp ?? null;
  }
  const isOnline = estPositionFraiche(merged, maintenant);
  return { ...merged, is_online: isOnline, is_stale: !isOnline };
}

/**
 * Folds a batch of tracking points into a vehicle list in one pass, so a
 * fleet message costs a single render whatever the number of trucks in it.
 */
export function mergeFleetPoints(vehicles, points, maintenant = Date.now()) {
  const batch = Array.isArray(points) ? points.filter((p) => p && p.truck_id) : [];
  if (batch.length === 0) return vehicles;

  const next = Array.isArray(vehicles) ? vehicles.slice() : [];
  const indexByTruck = new Map();
  next.forEach((vehicle, index) => indexByTruck.set(vehicle.truck_id, index));
  let changed = false;

  batch.forEach((point) => {
    const index = indexByTruck.get(point.truck_id);
    if (index == null) {
      indexByTruck.set(point.truck_id, next.length);
      const isOnline = estPositionFraiche(point, maintenant);
      const vitesse = point.speed_kmh == null ? NaN : Number(point.speed_kmh);
      next.push({
        stopped_since:
          Number.isFinite(vitesse) && vitesse <= MOUVEMENT_VITESSE_MIN_KMH ? point.timestamp ?? null : null,
        ...point,
        // The stream names the tenant `tenant_id`; the REST snapshot the
        // screens filter on names it `organization_id`. A truck that first
        // appeared over the socket was invisible to the org filter.
        organization_id: point.organization_id ?? point.tenant_id,
        truck: point.truck || {},
        driver: point.driver || {},
        is_online: isOnline,
        is_stale: !isOnline,
      });
    } else {
      next[index] = mergeVehicle(next[index], point, maintenant);
    }
    changed = true;
  });

  return changed ? next : vehicles;
}

/**
 * Traînes de départ, lues dans l'instantané (`recent_track`, du plus récent au
 * plus ancien). Une traîne déjà tenue par le flux n'est pas écrasée.
 */
export function amorcerTraines(history, rows, limit = 200) {
  const lignes = Array.isArray(rows) ? rows : [];
  let next = history;
  lignes.forEach((ligne) => {
    const piste = Array.isArray(ligne?.recent_track) ? ligne.recent_track : null;
    if (!ligne?.truck_id || !piste || piste.length === 0) return;
    if (next[ligne.truck_id]?.length) return;
    if (next === history) next = { ...history };
    next[ligne.truck_id] = piste.slice(0, limit);
  });
  return next;
}

/** Appends a batch to per-truck trails, newest first, capped. */
export function appendTrails(history, points, limit = 200) {
  const batch = Array.isArray(points) ? points.filter((p) => p && p.truck_id) : [];
  if (batch.length === 0) return history;

  const next = { ...history };
  batch.forEach((point) => {
    if (point.latitude == null || point.longitude == null) return;
    const existing = next[point.truck_id] || [];
    next[point.truck_id] = [[point.latitude, point.longitude], ...existing].slice(
      0,
      limit,
    );
  });
  return next;
}

export default function useFleetStream({
  mode = 'fleet',
  tripId = null,
  token,
  enabled = true,
  onFleetUpdate,
  onGpsUpdate,
  onPoll,
  pollMs = 0,
  /** Intervalle du relevé quand le flux direct est actif (défaut : `pollMs`). */
  pollMsConnecte = null,
} = {}) {
  const [connected, setConnected] = useState(false);
  const [lastMessageAt, setLastMessageAt] = useState(null);

  // Handlers are re-created on every parent render; keeping them in refs means
  // the socket is torn down only when the connection itself changes.
  const fleetRef = useRef(onFleetUpdate);
  const gpsRef = useRef(onGpsUpdate);
  const pollRef = useRef(onPoll);
  fleetRef.current = onFleetUpdate;
  gpsRef.current = onGpsUpdate;
  pollRef.current = onPoll;

  const url = enabled ? buildStreamUrl({ mode, tripId, token }) : null;

  useEffect(() => {
    if (!url) return undefined;

    let socket = null;
    let reconnectTimer = null;
    let keepalive = null;
    let attempt = 0;
    let disposed = false;

    const clearTimers = () => {
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (keepalive) window.clearInterval(keepalive);
      reconnectTimer = null;
      keepalive = null;
    };

    const connect = () => {
      if (disposed) return;
      try {
        socket = new WebSocket(url);
      } catch {
        // A malformed URL must not take the page down with it: retry on the
        // same schedule as a dropped connection.
        reconnectTimer = window.setTimeout(connect, nextReconnectDelay(attempt++));
        return;
      }

      socket.onopen = () => {
        if (disposed) return;
        attempt = 0;
        setConnected(true);
        // The server tracks a last_pong per connection; an idle socket behind a
        // proxy is closed long before the fleet moves again.
        keepalive = window.setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'ping' }));
          }
        }, KEEPALIVE_MS);
      };

      socket.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (!message || message.type === 'pong') return;
        setLastMessageAt(new Date());
        if (message.type === 'fleet_update' && Array.isArray(message.data)) {
          fleetRef.current?.(message.data);
        } else if (message.type === 'gps_update' && message.data) {
          gpsRef.current?.(message.data);
        }
      };

      socket.onclose = () => {
        if (keepalive) window.clearInterval(keepalive);
        keepalive = null;
        setConnected(false);
        if (disposed) return;
        reconnectTimer = window.setTimeout(connect, nextReconnectDelay(attempt++));
      };
    };

    connect();

    return () => {
      disposed = true;
      clearTimers();
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
      setConnected(false);
    };
  }, [url]);

  const refresh = useCallback(() => {
    pollRef.current?.();
  }, []);

  /**
   * The snapshot poll keeps running even while the socket is up, and that is
   * deliberate: the stream only carries movement, whereas a new truck, a trip
   * that changes step or a truck falling silent only show up in the REST
   * snapshot. While the stream is up it may run slower (`pollMsConnecte`).
   *
   * `onPoll` is a dependency on purpose — it closes over the current filters,
   * so changing the organisation refetches at once instead of waiting out the
   * interval. Callers must therefore memoise it. A change of interval (stream
   * up or down) does not refetch at once: only the cadence changes.
   */
  useEffect(() => {
    if (!onPoll || !pollMs || !enabled) return undefined;
    onPoll();
    return undefined;
  }, [enabled, onPoll, pollMs]);

  const intervalle = connected && pollMsConnecte ? pollMsConnecte : pollMs;
  useEffect(() => {
    if (!pollRef.current || !intervalle || !enabled) return undefined;
    const interval = window.setInterval(() => pollRef.current?.(), intervalle);
    return () => window.clearInterval(interval);
  }, [intervalle, enabled]);

  return { connected, lastMessageAt, refresh };
}
